/**
 * EmptyState — consistent "no data" message.
 *
 * Replaces 6+ inline patterns like:
 *   <div className="text-eve-muted py-8 text-center">Sin datos.</div>
 *   <div className="text-center py-10 space-y-1">
 *     <div>Title</div>
 *     <div className="text-xs text-eve-muted-2 italic">Sub</div>
 *   </div>
 *
 * Variants:
 *   - inline: muted single-line, used inside cards
 *   - card:   bordered panel, used as a full section placeholder
 *   - hero:   centered with optional action button, used in dedicated sections
 */

import type { ReactNode } from 'react'

export type EmptyVariant = 'inline' | 'card' | 'hero'

export function EmptyState({
  title,
  description,
  action,
  variant = 'card',
  className = '',
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  variant?: EmptyVariant
  className?: string
}) {
  if (variant === 'inline') {
    return (
      <div className={`text-eve-muted py-6 text-center text-sm italic ${className}`.trim()}>
        {title}
      </div>
    )
  }
  if (variant === 'card') {
    return (
      <div
        className={`bg-eve-bg border border-eve-border/60 rounded-md text-center py-10 px-4 space-y-1 ${className}`.trim()}
      >
        <div className="text-eve-muted">{title}</div>
        {description && (
          <div className="text-xs text-eve-muted-2 italic max-w-md mx-auto">
            {description}
          </div>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    )
  }
  // hero
  return (
    <div className={`text-center py-16 space-y-2 ${className}`.trim()}>
      <div className="font-display text-lg text-eve-muted">{title}</div>
      {description && (
        <div className="text-sm text-eve-muted-2 italic max-w-md mx-auto">
          {description}
        </div>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}
